/**
 * JP Pierre — Carrusel Bomber
 * Shoppable image carousel with product tag popups.
 * Custom element: <carrusel-bomber>
 *
 * Popups use position:fixed positioned via getBoundingClientRect
 * to avoid overflow clipping from the scroll track.
 */

class CarruselBomber extends HTMLElement {
  connectedCallback() {
    this.track    = this.querySelector('.cbomber__track');
    this.slides   = [...this.querySelectorAll('.cbomber__slide')];
    this.dotsWrap = this.querySelector('.cbomber__dots');
    this.prevBtn  = this.querySelector('.cbomber__btn--prev');
    this.nextBtn  = this.querySelector('.cbomber__btn--next');
    this.navEl    = this.querySelector('.cbomber__arrows');

    this.current         = 0;
    this.total           = this.slides.length;
    this.autoplayEnabled = this.dataset.autoplay === 'true';
    this.intervalMs      = parseInt(this.dataset.interval) || 5000;
    this.pauseOnHover    = this.dataset.pauseOnHover === 'true';
    this._timer          = null;
    this._resizeTimer    = null;
    this._scrollTimer    = null;

    if (this.total === 0 || !this.track) return;

    this.buildDots();
    this.updateDots();
    this.syncNavVisibility();
    this.bindCarouselEvents();
    this.initTags();
    this._syncVideos();

    if (this.autoplayEnabled) this.startAutoplay();
  }

  /* ---- Items visible ---- */

  getItemsVisible() {
    const val = getComputedStyle(this).getPropertyValue('--cbomber-current').trim();
    return Math.max(1, parseInt(val) || 1);
  }

  get maxIndex() {
    return Math.max(0, this.total - this.getItemsVisible());
  }

  /* ---- Navigation ---- */

  goTo(index, smooth = true) {
    this.closeAllTags();
    this.current = Math.max(0, Math.min(index, this.maxIndex));
    const slide = this.slides[0];
    if (!slide) return;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    this.track.scrollTo({
      left: this.current * (slide.offsetWidth + gap),
      behavior: smooth ? 'smooth' : 'instant',
    });
    this.updateDots();
    this._syncVideos();
  }

  _syncVideos() {
    const visible = this.getItemsVisible();
    this.slides.forEach((slide, i) => {
      const video = slide.querySelector('video');
      if (!video) return;
      const inView = i >= this.current && i < this.current + visible;
      if (inView) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  }

  next() {
    this.goTo(this.current >= this.maxIndex ? 0 : this.current + 1);
  }

  prev() {
    this.goTo(this.current <= 0 ? this.maxIndex : this.current - 1);
  }

  /* ---- Dots ---- */

  buildDots() {
    if (!this.dotsWrap) return;
    this.dotsWrap.innerHTML = '';
    for (let i = 0; i <= this.maxIndex; i++) {
      const btn = document.createElement('button');
      btn.className = 'cbomber__dot';
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-label', `Slide ${i + 1}`);
      btn.addEventListener('click', () => {
        this.goTo(i);
        if (this.autoplayEnabled) this.startAutoplay();
      });
      this.dotsWrap.appendChild(btn);
    }
  }

  updateDots() {
    if (!this.dotsWrap) return;
    this.dotsWrap.querySelectorAll('.cbomber__dot').forEach((dot, i) => {
      dot.classList.toggle('is-active', i === this.current);
    });
  }

  syncNavVisibility() {
    if (!this.navEl) return;
    this.navEl.style.display = this.maxIndex === 0 ? 'none' : '';
  }

  /* ---- Autoplay ---- */

  startAutoplay() {
    this.stopAutoplay();
    this._timer = setInterval(() => this.next(), this.intervalMs);
  }

  stopAutoplay() {
    if (this._timer) { clearInterval(this._timer); this._timer = null; }
  }

  /* ---- Carousel events ---- */

  bindCarouselEvents() {
    this.prevBtn?.addEventListener('click', () => {
      this.prev();
      if (this.autoplayEnabled) this.startAutoplay();
    });

    this.nextBtn?.addEventListener('click', () => {
      this.next();
      if (this.autoplayEnabled) this.startAutoplay();
    });

    if (this.pauseOnHover && this.autoplayEnabled) {
      this.addEventListener('mouseenter', () => this.stopAutoplay());
      this.addEventListener('mouseleave', () => this.startAutoplay());
    }

    let touchStartX = 0;
    this.track.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].clientX;
    }, { passive: true });

    this.track.addEventListener('touchend', (e) => {
      const diff = touchStartX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 40) {
        this.closeAllTags();
        diff > 0 ? this.next() : this.prev();
        if (this.autoplayEnabled) this.startAutoplay();
      }
    }, { passive: true });

    this.track.addEventListener('scroll', () => {
      clearTimeout(this._scrollTimer);
      this._scrollTimer = setTimeout(() => {
        const slide = this.slides[0];
        if (!slide) return;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        this.current = Math.round(this.track.scrollLeft / (slide.offsetWidth + gap));
        this.updateDots();
      }, 50);
    }, { passive: true });

    window.addEventListener('resize', () => {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => {
        this.closeAllTags();
        this.buildDots();
        this.syncNavVisibility();
        this.goTo(Math.min(this.current, this.maxIndex), false);
      }, 200);
    }, { passive: true });

    window.addEventListener('scroll', () => this.closeAllTags(), { passive: true });

    /* Keyboard navigation when carousel has focus */
    this.setAttribute('tabindex', '0');
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft')  { e.preventDefault(); this.prev(); if (this.autoplayEnabled) this.startAutoplay(); }
      if (e.key === 'ArrowRight') { e.preventDefault(); this.next(); if (this.autoplayEnabled) this.startAutoplay(); }
    });

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!this.autoplayEnabled) return;
          entry.isIntersecting ? this.startAutoplay() : this.stopAutoplay();
        });
      }, { threshold: 0.25 });
      io.observe(this);
    }
  }

  /* ---- Tag / popup interactions ---- */

  initTags() {
    /*
     * Root cause of "pointer shows, popup doesn't": .cbomber__tag has
     * transform: translate(-50%, -50%) which creates a new containing block
     * for position:fixed children, trapping the popup inside the tag element.
     * Fix: teleport every popup to <body> at init time so it lives outside
     * any transform context. We store a back-reference on the tag element.
     */
    this.querySelectorAll('.cbomber__tag').forEach((tag) => {
      const btn = tag.querySelector('.cbomber__tag-btn');
      const popup = tag.querySelector('.cbomber__popup');
      if (!btn || !popup) return;

      tag._popup = popup;
      document.body.appendChild(popup);

      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const isOpen = popup.classList.contains('is-open');
        this.closeAllTags();
        if (!isOpen) this._openPopup(btn, popup);
      });
    });

    document.addEventListener('click', (e) => {
      if (!e.target.closest('.cbomber__popup') && !e.target.closest('.cbomber__tag-btn')) {
        this.closeAllTags();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.closeAllTags();
    });
  }

  _openPopup(btn, popup) {
    const GAP = 14;
    const POPUP_W = window.innerWidth <= 749 ? 210 : 240;

    /* Measure height off-screen (popup is already in body, just reposition) */
    popup.style.cssText = `position:fixed;top:-9999px;left:-9999px;width:${POPUP_W}px;opacity:0;transition:none;z-index:400;`;
    const POPUP_H = popup.offsetHeight || 110;

    const btnRect = btn.getBoundingClientRect();
    const btnCenterX = btnRect.left + btnRect.width / 2;

    /* Vertical: prefer above; fall back to below if not enough space */
    const openAbove = btnRect.top > POPUP_H + GAP + 20;

    /* Horizontal: center on button, clamp to viewport edges */
    const idealLeft  = btnCenterX - POPUP_W / 2;
    const clampedLeft = Math.max(8, Math.min(idealLeft, window.innerWidth - POPUP_W - 8));

    /* Arrow offset: relative to popup's left edge */
    const arrowX = Math.max(14, Math.min(btnCenterX - clampedLeft, POPUP_W - 14));

    const top = openAbove
      ? btnRect.top  - POPUP_H - GAP
      : btnRect.bottom + GAP;

    popup.style.cssText = `position:fixed;z-index:400;width:${POPUP_W}px;left:${clampedLeft}px;top:${top}px;`;
    popup.style.setProperty('--cbomber-arrow-x', arrowX + 'px');

    popup.classList.remove('cbomber__popup--above', 'cbomber__popup--below');
    popup.classList.add(openAbove ? 'cbomber__popup--above' : 'cbomber__popup--below', 'is-open');
    btn.setAttribute('aria-expanded', 'true');
  }

  closeAllTags() {
    /* Popups are in <body> after teleport — search document-wide */
    document.querySelectorAll('.cbomber__popup.is-open').forEach((p) => {
      p.classList.remove('is-open', 'cbomber__popup--above', 'cbomber__popup--below');
      p.style.cssText = '';
    });
    this.querySelectorAll('.cbomber__tag-btn[aria-expanded="true"]').forEach((b) => {
      b.setAttribute('aria-expanded', 'false');
    });
  }

  disconnectedCallback() {
    this.stopAutoplay();
    /* Return teleported popups to their original tags */
    this.querySelectorAll('.cbomber__tag').forEach((tag) => {
      if (tag._popup) tag.appendChild(tag._popup);
    });
  }
}

customElements.define('carrusel-bomber', CarruselBomber);
